<template>
  <section class="book-visit-form">
    <h2>
      Book PV on Demand Call
    </h2>
    <router-link :to="{ name: 'calls' }" class="body-text">
      &laquo; Back to video calls
    </router-link>
    <div class="row mt-3">
      <div class="col-sm-6">
        <b-card>
          <b-alert class="p-1" variant="warning" :show="latestBookingDate !== null">The {{ 'prison' | term(true) }} has a latest
            booking date, therefore no dates will show for booking slots after {{ latestBookingDate | formatDate }}
          </b-alert>

          <div class="row">
            <div class="col-sm-6">
              <form-input
                v-model="prisoner.firstname"
                :label="$options.filters.term('prisoner', true) + ' First Name'"
              />
            </div>
            <div class="col-sm-6">
              <form-input
                v-model="prisoner.lastname"
                :label="$options.filters.term('prisoner', true) + ' Last Name'"
              />
            </div>
            <div class="col-sm-12">
              <form-input
                v-model="prisoner.prisoner_number"
                :label="$options.filters.term('prisoner', true) + ' Number'"
              />
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-sm-6">
              <form-input
                v-model="visitor.firstname"
                label="Visitor First Name"
              />
            </div>
            <div class="col-sm-6">
              <form-input
                v-model="visitor.lastname"
                label="Visitor Last Name"
              />
            </div>
            <div class="col-sm-12">
              <form-input
                v-model="visitor.email"
                label="Visitor Email"
              />
            </div>
            <div class="col-sm-12">
              <form-input
                v-model="visitor.phone"
                label="Visitor Phone Number (optional)"
              />
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-sm-12">
              <form-input
                v-model="passcode"
                type="text"
                autocomplete="off"
                label="Visit Passcode"
                description="This is the passcode the visitor will enter before being allowed into the visit."
              />
            </div>
            <div class="col-sm-12">
              <label>Booking Date</label>
              <b-form-datepicker
                v-model="bookingDate"
                :min="minDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              />
            </div>
            <div class="col-sm-12 mt-1">
              <SlotTimerPicker
                v-model="bookingSlot"
                :max-slot-length="prison.max_slot_length"
                @valid="(valid) => bookingSlotValid = valid"
              />
              <div class="form-group">
                <b-form-checkbox v-model="should_record" switch cy="record-call">
                  Record video call
                </b-form-checkbox>
              </div>
            </div>
          </div>

          <div class="row mt-1">
            <div class="col-sm-12">
              <b-btn variant="success"
                     class="full-width"
                     :disabled="!canSubmit()"
                     @click="bookIntake()"
              >
                Book PV on Demand Call
              </b-btn>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import FormInput from '@/components/form/FormInput';
import SlotTimerPicker from '@/components/bookVisit/SlotTimerPicker';
import moment from 'moment';
import VisitService from '@/services/VisitService';
import { logger } from '@/util/logger';
import { getErrorInfo } from '@/util/helpers';
import { ERROR_CODES } from '@/util/errorCodes';

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'book-intake-call',
  components: {
    FormInput,
    SlotTimerPicker,
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
  },
  data() {
    // Prison info
    const { prison } = this.$store.state.prison;
    let shouldRecord = false;
    let latestBookingDate = null;
    if (prison && prison.default_call_recording) {
      shouldRecord = true;
    }
    if (prison && prison.latest_booking_date) {
      latestBookingDate = moment(prison.latest_booking_date).subtract(1, 'day').toDate();
    }

    // Start date time
    let startTime = '10:00';
    let endTime = '10:15';

    const now = new Date();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    const formatMins = mins => {
      if (mins < 10) {
        return `0${mins}`;
      }
      return mins;
    }
    // Check if we're bang on a 5-minute block
    if (currentMin % 5 === 0) {
      const end = moment(now).add(15, 'minutes').toDate();
      // Set start and end time
      startTime = `${currentHour}:${formatMins(currentMin)}`;
      endTime = `${end.getHours()}:${formatMins(end.getMinutes())}`;
    } else {
      // If not we need to figure out when the next 5-minute block is
      let startMins = (currentMin - (currentMin % 5)) + 5;
      let startHour = currentHour;
      // We'll need to go to the next hour
      if (startMins >= 60) {
        startMins = 0;
        startHour += 1;
      }
      // Create a date with these hours and mins
      const startDate = new Date();
      startDate.setHours(startHour)
      startDate.setMinutes(startMins);
      // Add 15 mins on to this date
      const end = moment(startDate).add(15, 'minutes').toDate();
      // Set start and end time
      startTime = `${startHour}:${formatMins(startMins)}`;
      endTime = `${end.getHours()}:${formatMins(end.getMinutes())}`;
    }

    return {
      submitting: false,
      minDate: moment().format('YYYY-MM-DD'),
      prisoner: {
        firstname: '',
        lastname: '',
        prisoner_number: '',
      },
      visitor: {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
      },
      bookingSlotValid: true,
      bookingSlot: {
        startTime,
        endTime,
      },
      passcode: '',
      bookingDate: moment(now).format('YYYY-MM-DD'),
      should_record: shouldRecord,
      latestBookingDate,
    }
  },
  methods: {
    canSubmit() {
      let valid = true;
      if (this.prisoner.firstname.length < 1) {
        valid = false;
      }
      if (this.prisoner.lastname.length < 1) {
        valid = false;
      }
      if (this.prisoner.prisoner_number.length < 1) {
        valid = false;
      }
      if (this.visitor.firstname.length < 1) {
        valid = false;
      }
      if (this.visitor.lastname.length < 1) {
        valid = false;
      }
      if (this.visitor.email.length < 1) {
        valid = false;
      }
      if (this.passcode.length < 1) {
        valid = false;
      }
      if (!this.bookingDate) {
        valid = false;
      }
      if (this.submitting) {
        return false;
      }
      return valid && this.bookingSlotValid
    },
    async bookIntake() {
      console.log('>> book intake');
      console.log('>> book intake: ', this.bookingDate);
      const dayOfWeek = moment(this.bookingDate).format('dddd');
      const dto = {
        visitor: this.visitor,
        prisoner: this.prisoner,
        passcode: this.passcode,
        ad_hoc_slot: {
          slot_begin: this.bookingSlot.startTime,
          slot_end: this.bookingSlot.endTime,
          day_of_week: dayOfWeek,
        },
        booking_date: new Date(this.bookingDate).toISOString(),
        should_record: this.should_record,
      };

      this.submitting = true;
      try {
        const res = await VisitService.bookIntakeVisit(dto);
        // eslint-disable-next-line no-underscore-dangle
        this.$router.push(`/calls/${res.data._id}/details`);
      } catch (err) {
        logger.error('Error booking intake visit', err, dto, 'BookIntakeVisitForm:bookIntake');
        console.error('Error booking intake visit: ', err);
        const errorMessage = getErrorInfo(err);
        let msg = 'Something went wrong booking the PV on Demand call. Please refresh the page and try again.';
        if (errorMessage === ERROR_CODES.PRISON.NOT_ALLOWED_TO_BOOK) {
          msg = 'Cannot book as PV on Demand Calls are not currently enabled.';
        }
        this.$toast.error(msg, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.submitting = false;
      }
    },
  },
}
</script>
